/**
 * The steps every modal sheet shares — the Tab trap, the keydown, the <html>+<body> scroll lock,
 * the opener to hand focus back to, and the swipe-to-close guards. Moved out of SG's community
 * `BottomSheet` (2026-10-24) so the article conversation's `layout/ConversationSheet` (both
 * brands) runs the same code; `BottomSheet` re-exports them so its importers are unchanged.
 * One copy of the lock (Constraint 78a71681): never write a second.
 */

export const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'

/** Tab / Shift+Tab past the last / first focusable control of `box` wraps around inside it. */
export function trapTab(
  e: Pick<KeyboardEvent, 'key' | 'shiftKey' | 'preventDefault'>,
  box: Pick<HTMLElement, 'querySelectorAll'>,
  active: Element | null = typeof document === 'undefined' ? null : document.activeElement
): void {
  if (e.key !== 'Tab') {
    return
  }
  const items = Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE))
  if (items.length === 0) {
    return
  }
  const first = items[0]
  const last = items[items.length - 1]
  // Focus on the panel itself (its first frame) or outside it: Tab enters at the first control,
  // Shift+Tab at the last — never past the sheet.
  const inside = items.some(item => item === active)
  if (e.shiftKey && (active === first || !inside)) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && (active === last || !inside)) {
    e.preventDefault()
    first.focus()
  }
}

/** The sheet's keydown: Escape closes (when the sheet owns Escape), Tab is trapped. */
export function sheetKeyDown(
  e: Pick<KeyboardEvent, 'key' | 'shiftKey' | 'preventDefault'>,
  box: Pick<HTMLElement, 'querySelectorAll'>,
  onClose: () => void,
  closeOnEscape: boolean,
  active?: Element | null
): void {
  if (e.key === 'Escape') {
    if (closeOnEscape) {
      e.preventDefault()
      onClose()
    }
    return
  }
  trapTab(e, box, active)
}

interface Styled {
  style: { overflow: string }
}

/**
 * Locks the page scroll on <html> and <body> and returns the restore. Both: the shared
 * `:root { overflow-x: hidden }` makes <html> the scrolling element, so a body-only lock leaves
 * the page scrolling under the sheet (Constraint cc9ce959). The restore puts back whatever inline
 * value each had.
 */
export function lockPageScroll(doc: { body: Styled; documentElement: Styled }): () => void {
  const { body, documentElement: root } = doc
  const overflow = body.style.overflow
  const rootOverflow = root.style.overflow
  body.style.overflow = 'hidden'
  root.style.overflow = 'hidden'
  return () => {
    body.style.overflow = overflow
    root.style.overflow = rootOverflow
  }
}

/** The control a press or key last landed on (capture phase, document-wide). WebKit does not
 *  focus a button on tap, so at the sheet's first render `document.activeElement` is <body>; the
 *  sheet then hands focus back to this trigger instead. */
let lastTrigger: Element | null = null
const TRIGGER = 'button, a[href], [role="button"], [tabindex]:not([tabindex="-1"])'
if (typeof document !== 'undefined') {
  const note = (e: Event): void => {
    const target = e.target instanceof Element ? e.target.closest(TRIGGER) : null
    if (target) {
      lastTrigger = target
    }
  }
  document.addEventListener('click', note, true)
  document.addEventListener('keydown', note, true)
}

/** Where focus goes back on close: the focused control, else (nothing focused — a WebKit tap)
 *  the control the opening press or key landed on. */
export function sheetOpener(
  active: Element | null,
  trigger: Element | null,
  body: Element | null
): Element | null {
  return active && active !== body ? active : trigger ?? active
}

/** Is anything between the touch and the sheet (both included) scrolled down? Then a drag down
 *  is the reader scrolling back up, not closing — whether the sheet scrolls or a child of it
 *  does (`contentScroll`). */
export function scrolledFrom(
  target: Pick<Element, 'scrollTop' | 'parentElement'> | null,
  sheet: Pick<Element, 'scrollTop'>
): boolean {
  let node: Pick<Element, 'scrollTop' | 'parentElement'> | null = target
  while (node) {
    if (node.scrollTop > 0) {
      return true
    }
    if (node === sheet) {
      return false
    }
    node = node.parentElement
  }
  return sheet.scrollTop > 0
}

/** A swipe that ends `dy` px lower after `ms`: far enough, or a quick flick, closes the sheet. */
export function swipeCloses(dy: number, ms: number): boolean {
  if (dy >= 96) {
    return true
  }
  return dy >= 32 && ms > 0 && dy / ms >= 0.5
}

/** The control the last press or key landed on — `sheetOpener`'s fallback for a WebKit tap. */
export function lastSheetTrigger(): Element | null {
  return lastTrigger
}
